// =============================================================================
// enemyHud.ts — Geometria pura da HUD de inimigo (barra de HP + anel de elite).
// Usada pelo Renderer no hot path e pelos testes: uma fonte, zero Pixi aqui.
// =============================================================================

/** Altura fixa da barrinha (px em espaço local do container do inimigo). */
export const ENEMY_HP_BAR_H = 4;

/** Larguras por tipo — mesmos números que o Graphics antigo usava. */
export const ENEMY_HP_BAR_W = {
  normal: 24,
  elite: 30,
  boss: 60,
} as const;

/** Meio-diâmetro do anel na textura pré-renderizada (px na textura). */
export const ELITE_RING_TEX_RADIUS = 28;

/** Cor de preenchimento da barra (tinto no sprite fill). */
export const ENEMY_HP_FILL_COLOR = 0xd42a2a;
/** Cor do fundo da barra. */
export const ENEMY_HP_BG_COLOR = 0x000000;
/** Cor do anel de elite. */
export const ELITE_RING_COLOR = 0xffd117;

export interface EnemyHpBarLayout {
  barW: number;
  barH: number;
  /** Centro vertical da barra em Y local (negativo = acima do corpo). */
  y: number;
  /** Largura do preenchimento vermelho (proporcional a hpFrac clampado). */
  fillW: number;
  /** X da borda esquerda da barra (âncora do container em 0). */
  leftX: number;
  /** Deve desenhar a barra? (só quando já tomou dano). */
  visible: boolean;
}

/** Largura da barra conforme boss / elite / normal. */
export function enemyHpBarWidth(isBoss: boolean, elite: boolean): number {
  if (isBoss) return ENEMY_HP_BAR_W.boss;
  if (elite) return ENEMY_HP_BAR_W.elite;
  return ENEMY_HP_BAR_W.normal;
}

/** Y da barra em função da altura do body (acompanha pop de escala). */
export function enemyHpBarY(bodyHeight: number): number {
  return -bodyHeight * 0.75;
}

/**
 * Layout completo da barrinha a partir do estado do inimigo.
 * hpFrac 1 = full life → barra escondida (visible false).
 */
export function layoutEnemyHpBar(opts: {
  hpFrac: number;
  bodyHeight: number;
  isBoss: boolean;
  elite: boolean;
}): EnemyHpBarLayout {
  const hpFrac = Math.min(1, Math.max(0, opts.hpFrac));
  const barW = enemyHpBarWidth(opts.isBoss, opts.elite);
  const barH = ENEMY_HP_BAR_H;
  const y = enemyHpBarY(opts.bodyHeight);
  const visible = hpFrac < 1;
  const fillW = visible ? barW * hpFrac : 0;
  return { barW, barH, y, fillW, leftX: -barW / 2, visible };
}

/**
 * Escala do sprite do anel pra o raio visual bater com o body
 * (raio desejado = bodyHeight * 0.55, igual ao Graphics antigo).
 */
export function eliteRingScale(bodyHeight: number, texRadius = ELITE_RING_TEX_RADIUS): number {
  if (texRadius <= 0) return 0;
  return (bodyHeight * 0.55) / texRadius;
}

/** Elite flag bit no snapshot de inimigo (flags & 4). */
export function isEliteFlag(flags: number): boolean {
  return (flags & 4) !== 0;
}

/** Frozen flag bit (flags & 1). */
export function isFrozenFlag(flags: number): boolean {
  return (flags & 1) !== 0;
}

/** Ghost flag bit (flags & 16). */
export function isGhostFlag(flags: number): boolean {
  return (flags & 16) !== 0;
}
